/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Actionable, Card, Text, View } from '@az/starc-ui';
import Image, { akamaiLoader } from 'src/components/NextImage';
import styles from './styles.module.scss';
import cx from 'classnames';
import { type ProductModel } from '@/api/types/products-types';
import { useEffect, useMemo, useState } from 'react';
import { useGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import Skeleton from '@/components/Skeleton/Skeleton';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { useFeatureFlag } from '@/features/globalConfig';
import { formatPrice } from '@/utils/validator/formatPrice';

type Option = {
  selected: boolean;
  value: string;
  imageUrl?: string | undefined;
};

type Props = {
  options: Option[] | undefined;
  handleClick: (a: Option) => void;
  choices?: ProductModel['choices'];
  optionName: string;
  scrollLeftSignal: number;
  enabledOptions: string[];
  availableOptions?: ProductModel['availableOptions'];
  shouldShowSkeleton?: boolean;
};

type Choice = {
  choice: {
    itemId: string;
    productDetailsPageUrl: string;
    [key: string]: string;
  };
};

const PAGE_SIZE = 12;
const normalize = (s: string) => s.trim().toLowerCase();

const getChoiceProductName = (c: Choice, optionName: string): string | null => {
  return c?.choice?.[optionName] ?? null;
};

const getAriaLabelForVariant = (
  option: Option,
  price: number | undefined,
  isPriceLoading: boolean,
): string => {
  if (isPriceLoading) {
    return `${option.value}, Price loading${option.selected ? ' selected' : ''}`;
  }
  if (price !== undefined) {
    return `${option.value}, $${price.toFixed(2)}${option.selected ? ' selected' : ''}`;
  }
  return `${option.value}, ${option.selected ? ' selected' : ''}`;
};

export const SuperSkusImagesSelector = ({
  options = [],
  choices = [],
  scrollLeftSignal,
  handleClick,
  optionName,
  enabledOptions,
  availableOptions,
  shouldShowSkeleton = false,
}: Props) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isSalesPricingDisplayEnhancementsEnabled =
    useFeatureFlag('SALES_PRICING_DISPLAY_ENHANCEMENTS_ENABLED') === 'true';
  const selectedOtherOptionValues = useMemo(() => {
    const selected: Record<string, string> = {};
    if (!availableOptions) {
      return selected;
    }

    for (const [key, opts] of Object.entries(availableOptions)) {
      if (key === optionName) {
        continue;
      }
      const selectedOpt = opts?.find((opt) => opt.selected);
      if (selectedOpt) {
        selected[key] = selectedOpt.value;
      }
    }
    return selected;
  }, [availableOptions, optionName]);

  const productNameToSkuId = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of choices as unknown as Choice[]) {
      const skuId = c?.choice?.itemId;
      const productName = getChoiceProductName(c, optionName);

      const matchesAllSelectedOptions = Object.entries(selectedOtherOptionValues).every(
        ([key, value]) => c?.choice?.[key] === value,
      );

      if (skuId && productName && matchesAllSelectedOptions) {
        map.set(normalize(productName), skuId);
      }
    }
    return map;
  }, [choices, optionName, selectedOtherOptionValues]);

  const total = options.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const page = Math.min(scrollLeftSignal, maxPage);

  const end = Math.min(total, (page + 1) * PAGE_SIZE);

  const visibleOptions = useMemo(() => options.slice(0, end), [options, end]);
  const { data: storeDetailsData } = useStoreDetailsData();
  const storeNumber = storeDetailsData?.storeNumber;

  const [skuIdToPrice, setSkuIdToPrice] = useState<Record<string, number>>({});
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());

  const pageSkuIds = useMemo(() => {
    // Prioritize fetching the current page first, then backfill earlier unfetched pages
    let pageToFetch = page;

    // If current page is not fetched, fetch it first (priority for fast scrolling)
    if (!fetchedPages.has(page)) {
      pageToFetch = page;
    } else {
      // Current page already fetched, backfill earlier pages if needed
      for (let p = 0; p < page; p++) {
        if (!fetchedPages.has(p)) {
          pageToFetch = p;
          break;
        }
      }
    }

    const pageStart = pageToFetch * PAGE_SIZE;
    const pageEnd = Math.min(total, (pageToFetch + 1) * PAGE_SIZE);
    const slice = options.slice(pageStart, pageEnd);
    const ids: string[] = [];

    for (const opt of slice) {
      const skuId = productNameToSkuId.get(normalize(opt.value));
      if (skuId) {
        ids.push(skuId);
      }
    }

    return Array.from(new Set(ids));
  }, [options, page, total, productNameToSkuId, fetchedPages]);

  const skuDetailsResult = useGetProductSkuPrice({
    skuNumbers: pageSkuIds,
    storeNumber: storeNumber,
    enabled: pageSkuIds.length > 0 && Boolean(storeNumber),
  });

  // Call deals endpoint to get deal/cross price information for SKUs
  const dealSkuDetailsResult = useProductSkuDeals({
    skuIds: pageSkuIds,
    enabled: pageSkuIds.length > 0,
  });

  useEffect(() => {
    const pricingRecord = skuDetailsResult?.data;
    if (!pricingRecord) {
      return;
    }

    setSkuIdToPrice((prev) => {
      const updated = { ...prev };
      for (const [skuId, pricing] of Object.entries(pricingRecord)) {
        const corePrice = pricing?.corePrice ?? 0;
        const price =
          corePrice > 0
            ? (pricing?.retailPrice ?? pricing?.totalPrice)
            : (pricing?.totalPrice ?? pricing?.retailPrice);
        if (price != null) {
          updated[skuId] = price;
        }
      }
      return updated;
    });

    // Mark the page as fetched
    if (Object.keys(pricingRecord).length > 0) {
      setFetchedPages((prev) => new Set(prev).add(page));
    }
  }, [skuDetailsResult?.data, page]);

  // State map for skuId to discountProductPrice
  const [skuIdToDiscountPrice, setSkuIdToDiscountPrice] = useState<Record<string, number>>({});

  useEffect(() => {
    const dealDetails = dealSkuDetailsResult?.data;
    if (!dealDetails || !Array.isArray(dealDetails)) {
      return;
    }

    setSkuIdToDiscountPrice((prev) => {
      const updated = { ...prev };

      for (const item of dealDetails) {
        const skuId = item?.skuId;
        const discountedPrice = item?.skuDealsInfo?.discountedPrice;
        if (skuId && discountedPrice != null) {
          const priceAsNumber = parseFloat(discountedPrice);
          if (!isNaN(priceAsNumber)) {
            updated[skuId] = priceAsNumber;
          }
        }
      }
      return updated;
    });
  }, [dealSkuDetailsResult?.data]);

  const productNameToPrice = useMemo(() => {
    const result: Record<string, number> = {};
    const dealsApiHasError = dealSkuDetailsResult?.isError;

    for (const opt of visibleOptions) {
      const skuId = productNameToSkuId.get(normalize(opt.value));
      if (!skuId) {
        continue;
      }

      if (!dealsApiHasError) {
        const discountPrice = skuIdToDiscountPrice[skuId];
        if (discountPrice != null) {
          result[opt.value] = discountPrice;
          continue;
        }
      }

      const price = skuIdToPrice[skuId];
      if (price != null) {
        result[opt.value] = price;
      }
    }

    return result;
  }, [
    visibleOptions,
    productNameToSkuId,
    skuIdToPrice,
    skuIdToDiscountPrice,
    dealSkuDetailsResult?.isError,
  ]);

  const getProductName = (productName: string): string => {
    const cut = productName.replace(/\s*(?:&amp;|[/;&]).*$/i, '');
    return cut.trim().split(/\s+/).slice(0, 3).join(' ');
  };

  const hasAnyDiscount = useMemo(() => {
    // If deals API failed, don't show any discounts
    if (dealSkuDetailsResult?.isError) {
      return false;
    }

    return visibleOptions.some((opt) => {
      const skuId = productNameToSkuId.get(normalize(opt.value));
      if (!skuId) {
        return false;
      }

      const originalPrice = skuIdToPrice[skuId];
      const discountPrice = skuIdToDiscountPrice[skuId];

      return originalPrice != null && discountPrice != null && originalPrice > discountPrice;
    });
  }, [
    visibleOptions,
    productNameToSkuId,
    skuIdToPrice,
    skuIdToDiscountPrice,
    dealSkuDetailsResult?.isError,
  ]);

  return (
    <>
      {visibleOptions.map((option, index) => {
        const price = productNameToPrice[option.value];
        const isPriceLoading = skuDetailsResult?.isPending ?? false;

        if (shouldShowSkeleton && !isDesktop) {
          return (
            <View
              key={option.value}
              direction="column"
              className={cx(styles.optionContainer, {
                [styles.optionContainerWithDiscount]: hasAnyDiscount,
              })}
            >
              <Skeleton width={82} height={94} animation="wave" variant="rect" />
              <Skeleton
                width={82}
                height={17}
                animation="wave"
                variant="rect"
                className={styles.optionPrice}
              />
            </View>
          );
        }

        const skuId = productNameToSkuId.get(normalize(option.value));

        const originalPrice = skuId ? skuIdToPrice[skuId] : null;
        const discountPrice = skuId ? skuIdToDiscountPrice[skuId] : null;

        let discountDisplay: string | null = null;
        if (
          !dealSkuDetailsResult?.isError &&
          originalPrice != null &&
          discountPrice != null &&
          originalPrice > discountPrice
        ) {
          const discountAmount = originalPrice - discountPrice;
          const discountPercentage = Math.floor((discountAmount / originalPrice) * 100);

          discountDisplay =
            isSalesPricingDisplayEnhancementsEnabled && discountAmount > discountPercentage
              ? `$${formatPrice(discountAmount)}`
              : `${discountPercentage}%`;
        }

        return (
          <View
            key={option.value}
            direction="column"
            className={cx(styles.optionContainer, {
              [styles.optionContainerWithDiscount]: hasAnyDiscount,
            })}
          >
            <Card
              className={cx(styles.imagesCard, {
                [styles.disabled]: !enabledOptions.includes(option.value),
              })}
            >
              <Actionable
                onClick={() => handleClick(option)}
                attributes={{
                  'aria-label': getAriaLabelForVariant(option, price, isPriceLoading),
                  ...(option.selected && { 'aria-current': 'true' }),
                }}
                disabled={!enabledOptions.includes(option.value)}
                className={cx(
                  styles.ImagesButton,
                  {
                    [styles.selectedImagesButton]: option.selected,
                    [styles.disabled]: !enabledOptions.includes(option.value),
                  },
                  styles.loadingInformationContainer,
                )}
              >
                <View className={styles.imageCard}>
                  {option.imageUrl ? (
                    <Image
                      src={option.imageUrl}
                      alt={`Product variant image ${index + 1}. ${option.selected ? ' selected' : ''}`}
                      role="presentation"
                      width={60}
                      height={44}
                      className={styles.image}
                      loader={akamaiLoader}
                      unoptimized={false}
                    />
                  ) : null}

                  <View>
                    <Text attributes={{ 'aria-hidden': 'true' }} className={styles.productName}>
                      {getProductName(option.value)}
                    </Text>
                  </View>
                </View>
              </Actionable>
            </Card>

            {isPriceLoading ? (
              <Skeleton
                width={82}
                height={17}
                variant="rect"
                data-testid="st-skeleton"
                className={styles.optionPrice}
              />
            ) : (
              <>
                {price != null && (
                  <Text className={styles.optionPrice}>
                    {'$'}
                    {price != null ? price.toFixed(2) : ''}
                  </Text>
                )}
                {discountDisplay != null && (
                  <Text className={styles.discountPercentage}>Save {discountDisplay}</Text>
                )}
              </>
            )}
          </View>
        );
      })}
    </>
  );
};

export default SuperSkusImagesSelector;
